import { i as OTelService, n as EXTENSION_ACTIVITY_EVENT, r as ExtensionActivitySnowplowTracker, t as EXTENSION_ACTIVITY_CATEGORY } from "./extension_activity_tracker-BO5QIfPu.mjs";
import { Injectable, createInterfaceId } from "@gitlab/needle";
import { GitLabApiService, UserService, getLanguageServerVersion, ifVersionGte } from "@gitlab-org/core";
import { buildStructEvent, trackerCore } from "@snowplow/tracker-core";
import { v4 } from "uuid";
import Ajv from "ajv-draft-04";
import { isEqual } from "lodash-es";
import { LsFetch } from "@gitlab-org/fetch";
import { ConfigService } from "@gitlab-org/config";
import { Logger, withPrefix } from "@gitlab-org/logging";
//#region \0rolldown/runtime.js
var __defProp = Object.defineProperty;
var __exportAll = (all, no_symbols) => {
	let target = {};
	for (var name in all) __defProp(target, name, {
		get: all[name],
		enumerable: true
	});
	if (!no_symbols) __defProp(target, Symbol.toStringTag, { value: "Module" });
	return target;
};
//#endregion
//#region src/trackers/duo_chat_snowplow_tracker.ts
const DUO_CHAT_CATEGORY = "ask_gitlab_chat";
let DUO_CHAT_EVENT = /* @__PURE__ */ function(DUO_CHAT_EVENT) {
	DUO_CHAT_EVENT["BTN_CLICK"] = "click_button";
	DUO_CHAT_EVENT["TERMINAL_ACTION_CLICKED"] = "terminal_tool_window_action_clicked";
	return DUO_CHAT_EVENT;
}({});
const isTrackFeedbackContext = (obj) => {
	return typeof obj === "object" && obj !== null && ("improveWhat" in obj ? !obj.improveWhat || typeof obj.improveWhat === "string" : false) && ("didWhat" in obj ? !obj.didWhat || typeof obj.didWhat === "string" : false) && ("feedbackChoices" in obj ? !obj.feedbackChoices || Array.isArray(obj.feedbackChoices) && obj.feedbackChoices.every((choice) => typeof choice === "string") : false);
};
const DuoChatSnowplowTracker = createInterfaceId("DuoChatSnowplowTracker");
//#endregion
//#region src/trackers/duo_workflow_instance_tracker.ts
let DUO_WORKFLOW_EVENT = /* @__PURE__ */ function(DUO_WORKFLOW_EVENT) {
	DUO_WORKFLOW_EVENT["START"] = "start_duo_workflow_execution";
	DUO_WORKFLOW_EVENT["FINISH"] = "finish_duo_workflow_execution";
	DUO_WORKFLOW_EVENT["RETRY"] = "retry_duo_workflow_execution";
	return DUO_WORKFLOW_EVENT;
}({});
const DuoWorkflowTracker = createInterfaceId("DuoWorkflowTracker");
//#endregion
//#region src/trackers/duo_agent_platform_tracker.ts
const DUO_AGENT_PLATFORM_CATEGORY = "duo_agent_platform";
let DuoAgentPlatformEvent = /* @__PURE__ */ function(DuoAgentPlatformEvent) {
	DuoAgentPlatformEvent["WorkflowStopped"] = "workflow_stopped";
	DuoAgentPlatformEvent["TimeToFirstTokenStreamed"] = "time_to_first_token_streamed";
	DuoAgentPlatformEvent["TimeToAwaitingUserInput"] = "time_to_awaiting_user_input";
	DuoAgentPlatformEvent["StreamingTime"] = "streaming_time";
	DuoAgentPlatformEvent["UserFeedback"] = "ai_duo_agentic_chat_feedback_submitted";
	DuoAgentPlatformEvent["ToolApprovalSubmitted"] = "ai_duo_agentic_tool_approval_submitted";
	return DuoAgentPlatformEvent;
}({});
let PerformanceInteractionType = /* @__PURE__ */ function(PerformanceInteractionType) {
	PerformanceInteractionType["InitialRequest"] = "initial_request";
	PerformanceInteractionType["Continuation"] = "continuation";
	PerformanceInteractionType["ToolApproval"] = "tool_approval";
	PerformanceInteractionType["ToolRejection"] = "tool_rejection";
	PerformanceInteractionType["PlanApproval"] = "plan_approval";
	return PerformanceInteractionType;
}({});
const DuoAgentPlatformTracker = createInterfaceId("DuoAgentPlatformTracker");
//#endregion
//#region src/constants.ts
const SAAS_INSTANCE_URL = "https://gitlab.com";
const TELEMETRY_NOTIFICATION = "$/gitlab/telemetry";
const GC_TIME = 6e4;
//#endregion
//#region src/snowplow/constants.ts
const DEFAULT_TRACKING_ENDPOINT = "https://snowplowprd.trx.gitlab.net";
const DEFAULT_SNOWPLOW_OPTIONS = {
	appId: "gitlab_ide_extension",
	timeInterval: 5e3,
	maxItems: 10,
	isEventForwarding: false
};
const EVENT_VALIDATION_ERROR_MSG = `Telemetry event context is not valid  - event won't be tracked.`;
const MIN_VERSION_FOR_EVENT_FORWARDING = "18.0.0";
const MIN_VERSION_FOR_GITLAB_STANDARD_CONTEXT = "19.0.0";
const IDE_EXTENSION_VERSION_SCHEMA = "iglu:com.gitlab/ide_extension_version/jsonschema/1-1-0";
function buildSnowplowClientContextData(context) {
	return {
		ide_name: context?.ide?.name ?? null,
		ide_vendor: context?.ide?.vendor ?? null,
		ide_version: context?.ide?.version ?? null,
		extension_name: context?.extension?.name ?? null,
		extension_version: context?.extension?.version ?? null,
		language_server_version: getLanguageServerVersion() ?? null
	};
}
function createSnowplowClientContext() {
	return {
		schema: IDE_EXTENSION_VERSION_SCHEMA,
		data: {}
	};
}
//#endregion
//#region src/snowplow/event_forwarding.ts
/**
* Determines if the GitLab instance supports event forwarding
* Event forwarding is supported for self-managed instances >= 18.0
* GitLab.com always routes through the monolith to include Standard Context
* @param instanceUrl - The GitLab instance URL
* @param instanceVersion - The GitLab instance version
* @returns true if the instance supports event forwarding, false otherwise
*/
function supportsEventForwarding(instanceUrl, instanceVersion) {
	if (instanceUrl.hostname === "gitlab.com") return true;
	return ifVersionGte(instanceVersion, MIN_VERSION_FOR_EVENT_FORWARDING, () => true, () => false);
}
/**
* Returns true if the GitLab instance supports standard context enrichment (>= 19.0.0).
* When supported, the monolith injects user identity fields into forwarded telemetry events.
* @param instanceVersion - The GitLab instance version
* @returns true if standard context enrichment is supported, false otherwise
*/
function supportsStandardContext(instanceVersion) {
	return ifVersionGte(instanceVersion, MIN_VERSION_FOR_GITLAB_STANDARD_CONTEXT, () => true, () => false);
}
/**
* Gets the event forwarder endpoint for a GitLab instance
* @param instanceUrl - The GitLab instance URL
* @returns The event forwarder endpoint URL
*/
function getEventForwarderEndpoint(instanceUrl) {
	return `${instanceUrl.origin}/-/collect_events`;
}
//#endregion
//#region src/snowplow/emitter.ts
var EmitterState = /* @__PURE__ */ function(EmitterState) {
	EmitterState["STARTED"] = "STARTED";
	EmitterState["STOPPING"] = "STOPPING";
	EmitterState["STOPPED"] = "STOPPED";
	return EmitterState;
}(EmitterState || {});
var Emitter = class {
	#trackingQueue = [];
	#callback;
	#timeInterval;
	#maxItems;
	#currentState;
	#timeout;
	constructor(timeInterval, maxItems, callback) {
		this.#maxItems = maxItems;
		this.#timeInterval = timeInterval;
		this.#callback = callback;
		this.#currentState = EmitterState.STOPPED;
	}
	add(data) {
		this.#trackingQueue.push(data);
		if (this.#trackingQueue.length >= this.#maxItems) this.#drainQueue().catch(() => {});
	}
	async #drainQueue() {
		if (this.#trackingQueue.length > 0) {
			const copyOfTrackingQueue = this.#trackingQueue.map((e) => e);
			this.#trackingQueue = [];
			await this.#callback(copyOfTrackingQueue);
		}
	}
	start() {
		this.#timeout = setTimeout(async () => {
			await this.#drainQueue();
			if (this.#currentState !== EmitterState.STOPPING) this.start();
		}, this.#timeInterval);
		this.#currentState = EmitterState.STARTED;
	}
	async stop() {
		this.#currentState = EmitterState.STOPPING;
		if (this.#timeout) clearTimeout(this.#timeout);
		this.#timeout = void 0;
		await this.#drainQueue();
	}
};
//#endregion
//#region src/snowplow/schemas/snowplow_schema-1-0-0.json
var snowplow_schema_1_0_0_exports = /* @__PURE__ */ __exportAll({
	$schema: () => $schema$2,
	allOf: () => allOf,
	default: () => snowplow_schema_1_0_0_default,
	description: () => description$2,
	id: () => id,
	self: () => self$2
});
var description$2 = "Meta-schema for self-describing JSON schema";
var allOf = [{
	"properties": { "self": {
		"type": "object",
		"properties": {
			"vendor": {
				"type": "string",
				"pattern": "^[a-zA-Z0-9-_.]+$"
			},
			"name": {
				"type": "string",
				"pattern": "^[a-zA-Z0-9-_]+$"
			},
			"format": {
				"type": "string",
				"pattern": "^[a-zA-Z0-9-_]+$"
			},
			"version": {
				"type": "string",
				"pattern": "^[0-9]+-[0-9]+-[0-9]+$"
			}
		},
		"required": [
			"vendor",
			"name",
			"format",
			"version"
		],
		"additionalProperties": false
	} },
	"required": ["self"]
}, { "$ref": "http://json-schema.org/draft-04/schema#" }];
var self$2 = {
	"vendor": "com.snowplowanalytics.self-desc",
	"name": "schema",
	"format": "jsonschema",
	"version": "1-0-0"
};
var $schema$2 = "http://iglucentral.com/schemas/com.snowplowanalytics.self-desc/schema/jsonschema/1-0-0#";
var id = "http://iglucentral.com/schemas/com.snowplowanalytics.self-desc/schema/jsonschema/1-0-0#";
var snowplow_schema_1_0_0_default = {
	description: description$2,
	allOf,
	self: self$2,
	$schema: $schema$2,
	id
};
//#endregion
//#region src/snowplow/snowplow_service.ts
let _initClass$3;
function _applyDecs$3(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$3(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$3(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$3(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$3(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$3(t) {
	var i = _toPrimitive$3(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$3(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$3(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$3(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
/**
* Adds the 'stm' parameter with the current time to the payload
* Stringify all payload values
* @param payload - The payload which will be mutated
*/
function preparePayload(payload) {
	const stringifiedPayload = {};
	Object.keys(payload).forEach((key) => {
		stringifiedPayload[key] = String(payload[key]);
	});
	stringifiedPayload.stm = (/* @__PURE__ */ new Date()).getTime().toString();
	return stringifiedPayload;
}
const SnowplowService = createInterfaceId("SnowplowService");
let _DefaultSnowplowServi;
(class {
	static {
		[_DefaultSnowplowServi, _initClass$3] = _applyDecs$3(this, [Injectable(SnowplowService, [
			LsFetch,
			ConfigService,
			GitLabApiService,
			Logger
		])], []).c;
	}
	/** Disable sending events when it's not possible. */
	#disabled = false;
	#emitter;
	#lsFetch;
	#logger;
	#apiService;
	#configService;
	#ajv = new Ajv({ strict: false });
	#options;
	#tracker;
	#subscriptions = [];
	constructor(lsFetch, configService, apiService, logger) {
		this.#lsFetch = lsFetch;
		this.#logger = logger;
		this.#apiService = apiService;
		this.#configService = configService;
		this.#ajv.addMetaSchema(snowplow_schema_1_0_0_exports);
		this.#subscriptions.push(configService.onConfigChange(async (config) => {
			const trackingUrl = config.telemetry?.trackingUrl;
			if (trackingUrl) await this.#reconfigure({ endpoint: trackingUrl });
		}), this.#apiService.onApiReconfigured(async (data) => {
			if (!data.isInValidState) return;
			await this.#updateEndpointBasedOnInstance();
		}));
		this.#configure({
			...DEFAULT_SNOWPLOW_OPTIONS,
			endpoint: this.#determineEndpoint(),
			isEventForwarding: this.#isEventForwardingEndpoint(),
			enabled: () => configService.get("telemetry.enabled") ?? true
		});
	}
	#determineEndpoint() {
		const customTrackingUrl = this.#configService.get("telemetry.trackingUrl");
		if (customTrackingUrl) return customTrackingUrl;
		if (this.#apiService.instanceInfo?.instanceUrl && this.#shouldUseEventForwarding()) return getEventForwarderEndpoint(this.#apiService.instanceInfo.instanceUrl);
		return DEFAULT_TRACKING_ENDPOINT;
	}
	#shouldUseEventForwarding() {
		if (!this.#apiService.instanceInfo) return false;
		return supportsEventForwarding(this.#apiService.instanceInfo.instanceUrl, this.#apiService.instanceInfo.instanceVersion);
	}
	#shouldIncludeStandardContext() {
		if (!this.#apiService.instanceInfo) return false;
		return supportsStandardContext(this.#apiService.instanceInfo.instanceVersion);
	}
	#isEventForwardingEndpoint() {
		if (this.#configService.get("telemetry.trackingUrl")) return false;
		return this.#apiService.instanceInfo ? this.#shouldUseEventForwarding() : false;
	}
	async #updateEndpointBasedOnInstance() {
		const newEndpoint = this.#determineEndpoint();
		if (newEndpoint !== this.#options.endpoint) await this.#reconfigure({
			endpoint: newEndpoint,
			isEventForwarding: this.#isEventForwardingEndpoint()
		});
	}
	#configure(options) {
		this.#options = options;
		this.#emitter = new Emitter(this.#options.timeInterval, this.#options.maxItems, this.#sendEvent.bind(this));
		this.#emitter.start();
		this.#tracker = trackerCore({ callback: this.#emitter.add.bind(this.#emitter) });
	}
	async #reconfigure(options) {
		const newOptions = {
			...this.#options,
			...options
		};
		if (!isEqual(this.#options, newOptions)) {
			await this.#emitter?.stop();
			this.#configure(newOptions);
		}
	}
	async trackStructuredEvent(event, context) {
		try {
			this.#tracker.track(buildStructEvent(event), context);
		} catch (error) {
			this.#logger.warn("Failed to track Snowplow event", error);
		}
	}
	validateContext(schema, data) {
		const valid = this.#ajv.validate(schema, data);
		if (!valid) {
			this.#logger.warn(EVENT_VALIDATION_ERROR_MSG);
			this.#logger.debug(`AJV validation issue:\nData: ${JSON.stringify(data, null, 2)}\nErrors: ${JSON.stringify(this.#ajv.errors, null, 2)}`);
		}
		return valid;
	}
	async stop() {
		await this.#emitter.stop();
	}
	dispose() {
		this.#subscriptions.forEach((s) => s.dispose());
		this.#subscriptions = [];
	}
	async #sendEvent(events) {
		if (!this.#options.enabled() || this.#disabled) return;
		try {
			const url = this.#options.isEventForwarding ? this.#options.endpoint : `${this.#options.endpoint}/com.snowplowanalytics.snowplow/tp2`;
			const data = {
				schema: "iglu:com.snowplowanalytics.snowplow/payload_data/jsonschema/1-0-4",
				data: events.map((event) => {
					const eventId = v4();
					event.add("eid", eventId);
					event.add("p", "app");
					event.add("tv", "js-gitlab");
					event.add("tna", "gl");
					event.add("aid", this.#options.appId);
					return preparePayload(event.build());
				})
			};
			const headers = { "Content-Type": "application/json" };
			if (this.#options.isEventForwarding && this.#shouldIncludeStandardContext()) {
				headers["X-GitLab-Editor-Telemetry"] = "1";
				if (this.#apiService.tokenInfo?.token) if (this.#apiService.tokenInfo.type === "oauth") headers.Authorization = `Bearer ${this.#apiService.tokenInfo.token}`;
				else headers["Private-Token"] = this.#apiService.tokenInfo.token;
			}
			const config = {
				headers,
				body: JSON.stringify(data)
			};
			const response = await this.#lsFetch.post(url, config);
			if (response.status !== 200) this.#logger.warn(`Could not send telemetry to snowplow, this warning can be safely ignored. status=${response.status}`);
		} catch (error) {
			let errorHandled = false;
			if (typeof error === "object" && "errno" in error) {
				const errObject = error;
				if ("errno" in errObject && errObject.errno === "ENOTFOUND") {
					this.#disabled = true;
					errorHandled = true;
					this.#logger.info("Disabling telemetry, unable to resolve endpoint address.");
				} else this.#logger.warn(JSON.stringify(errObject));
			}
			if (!errorHandled) {
				this.#logger.warn("Failed to send telemetry event, this warning can be safely ignored");
				this.#logger.warn(JSON.stringify(error));
			}
		}
	}
	static {
		_initClass$3();
	}
});
//#endregion
//#region src/snowplow/standard_context.ts
let _initClass$2;
function _applyDecs$2(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$2(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$2(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$2(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$2(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$2(t) {
	var i = _toPrimitive$2(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$2(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$2(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$2(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
const ENVIRONMENT_NAMES = {
	GITLAB_COM: "production",
	GITLAB_STAGING: "staging",
	GITLAB_ORG: "org",
	GITLAB_DEVELOPMENT: "development",
	GITLAB_SELF_MANAGED: "self-managed"
};
const ENVIRONMENT_URLS = {
	GITLAB_COM: "https://gitlab.com",
	GITLAB_STAGING: "https://staging.gitlab.com",
	GITLAB_ORG: "https://dev.gitlab.org",
	GITLAB_DEVELOPMENT: "http://localhost"
};
const STANDARD_CONTEXT_SCHEMA = "iglu:com.gitlab/gitlab_standard/jsonschema/1-1-1";
const DEFAULT_EVENT_SOURCE = "Language Server";
const StandardContext = createInterfaceId("StandardContext");
let _DefaultStandardConte;
(class {
	static {
		[_DefaultStandardConte, _initClass$2] = _applyDecs$2(this, [Injectable(StandardContext, [ConfigService, UserService])], []).c;
	}
	#source = DEFAULT_EVENT_SOURCE;
	#environment = ENVIRONMENT_NAMES.GITLAB_COM;
	#hostName = ENVIRONMENT_URLS.GITLAB_COM;
	#userService;
	constructor(configService, userService) {
		this.#userService = userService;
		this.#setConfigDataToContext(configService.get());
		configService.onConfigChange((config) => this.#setConfigDataToContext(config));
	}
	#setConfigDataToContext(config) {
		this.#source = config?.telemetry?.extension?.name ?? DEFAULT_EVENT_SOURCE;
		this.#hostName = config?.baseUrl ?? "https://gitlab.com";
		this.#environment = environmentFromHost(this.#hostName);
	}
	build(extra) {
		return {
			schema: STANDARD_CONTEXT_SCHEMA,
			data: {
				source: this.#source,
				extra,
				environment: this.#environment,
				host_name: this.#hostName,
				user_id: this.#userService.user?.restId ?? null
			}
		};
	}
	static {
		_initClass$2();
	}
});
function environmentFromHost(url) {
	const { GITLAB_COM, GITLAB_STAGING, GITLAB_ORG, GITLAB_DEVELOPMENT } = ENVIRONMENT_URLS;
	if (url === GITLAB_COM) return ENVIRONMENT_NAMES.GITLAB_COM;
	if (url === GITLAB_STAGING) return ENVIRONMENT_NAMES.GITLAB_STAGING;
	if (url === GITLAB_ORG) return ENVIRONMENT_NAMES.GITLAB_ORG;
	if (url.includes(GITLAB_DEVELOPMENT)) return ENVIRONMENT_NAMES.GITLAB_DEVELOPMENT;
	return ENVIRONMENT_NAMES.GITLAB_SELF_MANAGED;
}
//#endregion
//#region src/snowplow/schemas/standard_context_schema-1-1-1.json
var standard_context_schema_1_1_1_default = {
	$schema: "http://iglucentral.com/schemas/com.snowplowanalytics.self-desc/schema/jsonschema/1-0-0#",
	description: "Standard schema for a gitlab events",
	self: {
		"vendor": "com.gitlab",
		"name": "gitlab_standard",
		"version": "1-1-1",
		"format": "jsonschema"
	},
	type: "object",
	additionalProperties: false,
	required: ["environment"],
	properties: {
		"project_id": {
			"description": "ID of the associated project",
			"type": ["integer", "null"],
			"minimum": 0,
			"maximum": 2147483647
		},
		"namespace_id": {
			"description": "ID of the associated namespace",
			"type": ["integer", "null"],
			"minimum": 0,
			"maximum": 2147483647
		},
		"user_id": {
			"description": "ID of the associated user",
			"type": ["integer", "null"],
			"minimum": 0,
			"maximum": 2147483647
		},
		"is_gitlab_team_member": {
			"description": "Indicates if triggered by a GitLab team member",
			"type": ["boolean", "null"]
		},
		"feature_enabled_by_namespace_ids": {
			"description": "List of namespaces that allow the user to use the tracked feature ",
			"type": ["array", "null"],
			"items": {
				"description": "Namespace id that have the tracked feature enabled",
				"type": "integer",
				"minimum": 0,
				"maximum": 2147483647
			}
		},
		"environment": {
			"description": "Name of the source environment, such as `production` or `staging`",
			"type": "string",
			"maxLength": 32
		},
		"source": {
			"description": "Name of the source application, such as  `gitlab-rails` or `gitlab-javascript`",
			"type": ["string", "null"],
			"maxLength": 32
		},
		"plan": {
			"description": "Name of the plan, such as  `free`, `bronze`, `silver`, `premium`, `gold` or `ultimate`",
			"type": ["string", "null"],
			"maxLength": 32
		},
		"google_analytics_id": {
			"description": "Google Analytics ID from the marketing site",
			"type": ["string", "null"],
			"maxLength": 32
		},
		"context_generated_at": {
			"description": "Timestamp indicating moment when context was generated",
			"type": ["string", "null"],
			"maxLength": 64
		},
		"realm": {
			"type": ["string", "null"],
			"description": "Self-Managed, SaaS or Dedicated",
			"enum": [
				"self-managed",
				"saas",
				"dedicated",
				null
			]
		},
		"instance_id": {
			"type": ["string", "null"],
			"description": "ID of the GitLab instance where the request comes from",
			"maxLength": 255
		},
		"host_name": {
			"type": ["string", "null"],
			"description": "Host name of the GitLab instance where the request comes from",
			"maxLength": 255
		},
		"instance_version": {
			"type": ["string", "null"],
			"description": "Version of the GitLab instance where the request comes from",
			"maxLength": 255
		},
		"global_user_id": {
			"type": ["string", "null"],
			"description": "Globally unique user ID",
			"maxLength": 64
		},
		"correlation_id": {
			"type": ["string", "null"],
			"description": "Unique request id for each request",
			"maxLength": 64
		},
		"extra": {
			"description": "Any additional data associated with the event, in the form of key-value pairs",
			"type": ["object", "null"]
		}
	}
};
//#endregion
//#region src/schemas/ide_extension_version-1-1-0.json
var ide_extension_version_1_1_0_exports = /* @__PURE__ */ __exportAll({
	$schema: () => $schema,
	additionalProperties: () => additionalProperties,
	default: () => ide_extension_version_1_1_0_default,
	description: () => description,
	properties: () => properties,
	required: () => required,
	self: () => self,
	type: () => type
});
var $schema = "http://iglucentral.com/schemas/com.snowplowanalytics.self-desc/schema/jsonschema/1-0-0#";
var description = "Schema for a IDE and IDE extension version informations";
var self = {
	"vendor": "com.gitlab",
	"name": "ide_extension_version",
	"version": "1-1-0",
	"format": "jsonschema"
};
var type = "object";
var additionalProperties = false;
var required = [
	"ide_name",
	"ide_version",
	"ide_vendor",
	"extension_name",
	"extension_version",
	"language_server_version"
];
var properties = {
	"ide_name": {
		"description": "Name of the IDE, e.g. RubyMibe",
		"type": ["string", "null"],
		"maxLength": 32
	},
	"ide_version": {
		"description": "Version number of the IDE, e.g. 1.81.1",
		"type": ["string", "null"],
		"maxLength": 32
	},
	"ide_vendor": {
		"description": "Name of the IDEs vendor, e.g. Microsoft",
		"type": ["string", "null"],
		"maxLength": 32
	},
	"extension_name": {
		"description": "Name of the IDE extension, e.g. GitLab Workflow",
		"type": ["string", "null"],
		"maxLength": 32
	},
	"extension_version": {
		"description": "Version number of the IDE extension, e.g. 3.81.1",
		"type": ["string", "null"],
		"maxLength": 32
	},
	"language_server_version": {
		"description": "Version number of the Language Server, e.g. 3.9.0",
		"type": ["string", "null"],
		"maxLength": 32
	}
};
var ide_extension_version_1_1_0_default = {
	$schema,
	description,
	self,
	type,
	additionalProperties,
	required,
	properties
};
//#endregion
//#region src/default_snowplow_tracker.ts
var DefaultSnowplowTracker = class {
	#configService;
	#snowplowService;
	#standardContext;
	#logger;
	#eventCategory;
	#clientContext = createSnowplowClientContext();
	#enabled = true;
	constructor(configService, snowplowService, standardContext, logger, eventCategory) {
		this.#configService = configService;
		this.#snowplowService = snowplowService;
		this.#configService.onConfigChange((config) => this.#reconfigure(config));
		this.#standardContext = standardContext;
		this.#logger = withPrefix(logger, `[SnowplowTracker(${eventCategory})]`);
		this.#eventCategory = eventCategory;
	}
	#reconfigure(config) {
		const enabled = config.telemetry?.enabled;
		if (typeof enabled !== "undefined" && this.#enabled !== enabled) this.#enabled = enabled;
		this.#setClientContext({
			extension: config.telemetry?.extension,
			ide: config.telemetry?.ide
		});
	}
	#setClientContext(context) {
		this.#clientContext.data = buildSnowplowClientContextData(context);
	}
	async trackEvent(event, context) {
		if (!this.isEnabled()) return;
		if (!this.hasClientContext()) return;
		const structuredEvent = {
			category: this.#eventCategory,
			action: event
		};
		try {
			const extra = {};
			if (context) {
				for (const [key, value] of Object.entries(context)) if (value !== null && value !== void 0) extra[key] = String(value);
			}
			const standardContext = this.#standardContext.build(extra);
			const contexts = [standardContext, this.#clientContext];
			if (!this.#snowplowService.validateContext(standard_context_schema_1_1_1_default, standardContext.data)) {
				this.#logger.warn("Invalid standard context");
				return;
			}
			if (!this.#snowplowService.validateContext(ide_extension_version_1_1_0_exports, this.#clientContext?.data)) {
				this.#logger.warn("Invalid IDE extension context");
				return;
			}
			await this.#snowplowService.trackStructuredEvent(structuredEvent, contexts);
		} catch (error) {
			this.#logger.error(`Failed to track ${event}`, error);
		}
	}
	isEnabled() {
		return this.#enabled;
	}
	hasClientContext() {
		return Boolean(this.#clientContext.data.ide_name || this.#clientContext.data.extension_name);
	}
};
//#endregion
//#region src/extension_activity/extension_activity_snowplow_tracker.ts
let _initClass$1;
function _applyDecs$1(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName$1(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName$1(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey$1(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS$1(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey$1(t) {
	var i = _toPrimitive$1(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive$1(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName$1(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS$1(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
let _DefaultExtensionActi;
(class {
	static {
		[_DefaultExtensionActi, _initClass$1] = _applyDecs$1(this, [Injectable(ExtensionActivitySnowplowTracker, [
			ConfigService,
			SnowplowService,
			StandardContext,
			Logger
		])], []).c;
	}
	#snowplowTracker;
	constructor(configService, snowplowService, standardContext, logger) {
		this.#snowplowTracker = new DefaultSnowplowTracker(configService, snowplowService, standardContext, logger, EXTENSION_ACTIVITY_CATEGORY);
	}
	async trackEvent(event, context) {
		this.#snowplowTracker.trackEvent(event, context);
	}
	isEnabled() {
		return this.#snowplowTracker.isEnabled();
	}
	hasClientContext() {
		return this.#snowplowTracker.hasClientContext();
	}
	static {
		_initClass$1();
	}
});
//#endregion
//#region src/trackers/default_duo_agent_platform_tracker.ts
let _initClass;
function _applyDecs(e, t, n, r, o, i) {
	var a, c, u, s, f, l, p, d = Symbol.metadata || Symbol.for("Symbol.metadata"), m = Object.defineProperty, h = Object.create, y = [h(null), h(null)], v = t.length;
	function g(t, n, r) {
		return function(o, i) {
			n && (i = o, o = e);
			for (var a = 0; a < t.length; a++) i = t[a].apply(o, r ? [i] : []);
			return r ? i : o;
		};
	}
	function b(e, t, n, r) {
		if ("function" != typeof e && (r || void 0 !== e)) throw new TypeError(t + " must " + (n || "be") + " a function" + (r ? "" : " or undefined"));
		return e;
	}
	function applyDec(e, t, n, r, o, i, u, s, f, l, p) {
		function d(e) {
			if (!p(e)) throw new TypeError("Attempted to access private element on non-instance");
		}
		var h = [].concat(t[0]), v = t[3], w = !u, D = 1 === o, S = 3 === o, j = 4 === o, E = 2 === o;
		function I(t, n, r) {
			return function(o, i) {
				return n && (i = o, o = e), r && r(o), P[t].call(o, i);
			};
		}
		if (!w) {
			var P = {}, k = [], F = S ? "get" : j || D ? "set" : "value";
			if (f ? (l || D ? P = {
				get: _setFunctionName(function() {
					return v(this);
				}, r, "get"),
				set: function(e) {
					t[4](this, e);
				}
			} : P[F] = v, l || _setFunctionName(P[F], r, E ? "" : F)) : l || (P = Object.getOwnPropertyDescriptor(e, r)), !l && !f) {
				if ((c = y[+s][r]) && 7 !== (c ^ o)) throw Error("Decorating two elements with the same name (" + P[F].name + ") is not supported yet");
				y[+s][r] = o < 3 ? 1 : o;
			}
		}
		for (var N = e, O = h.length - 1; O >= 0; O -= n ? 2 : 1) {
			var T = b(h[O], "A decorator", "be", !0), z = n ? h[O - 1] : void 0, A = {}, H = {
				kind: [
					"field",
					"accessor",
					"method",
					"getter",
					"setter",
					"class"
				][o],
				name: r,
				metadata: a,
				addInitializer: function(e, t) {
					if (e.v) throw new TypeError("attempted to call addInitializer after decoration was finished");
					b(t, "An initializer", "be", !0), i.push(t);
				}.bind(null, A)
			};
			if (w) c = T.call(z, N, H), A.v = 1, b(c, "class decorators", "return") && (N = c);
			else if (H.static = s, H.private = f, c = H.access = { has: f ? p.bind() : function(e) {
				return r in e;
			} }, j || (c.get = f ? E ? function(e) {
				return d(e), P.value;
			} : I("get", 0, d) : function(e) {
				return e[r];
			}), E || S || (c.set = f ? I("set", 0, d) : function(e, t) {
				e[r] = t;
			}), N = T.call(z, D ? {
				get: P.get,
				set: P.set
			} : P[F], H), A.v = 1, D) {
				if ("object" == typeof N && N) (c = b(N.get, "accessor.get")) && (P.get = c), (c = b(N.set, "accessor.set")) && (P.set = c), (c = b(N.init, "accessor.init")) && k.unshift(c);
				else if (void 0 !== N) throw new TypeError("accessor decorators must return an object with get, set, or init properties or undefined");
			} else b(N, (l ? "field" : "method") + " decorators", "return") && (l ? k.unshift(N) : P[F] = N);
		}
		return o < 2 && u.push(g(k, s, 1), g(i, s, 0)), l || w || (f ? D ? u.splice(-1, 0, I("get", s), I("set", s)) : u.push(E ? P[F] : b.call.bind(P[F])) : m(e, r, P)), N;
	}
	function w(e) {
		return m(e, d, {
			configurable: !0,
			enumerable: !0,
			value: a
		});
	}
	return void 0 !== i && (a = i[d]), a = h(null == a ? null : a), f = [], l = function(e) {
		e && f.push(g(e));
	}, p = function(t, r) {
		for (var i = 0; i < n.length; i++) {
			var a = n[i], c = a[1], l = 7 & c;
			if ((8 & c) == t && !l == r) {
				var p = a[2], d = !!a[3], m = 16 & c;
				applyDec(t ? e : e.prototype, a, m, d ? "#" + p : _toPropertyKey(p), l, l < 2 ? [] : t ? s = s || [] : u = u || [], f, !!t, d, r, t && d ? function(t) {
					return _checkInRHS(t) === e;
				} : o);
			}
		}
	}, p(8, 0), p(0, 0), p(8, 1), p(0, 1), l(u), l(s), c = f, v || w(e), {
		e: c,
		get c() {
			var n = [];
			return v && [w(e = applyDec(e, [t], r, e.name, 5, n)), g(n, 1)];
		}
	};
}
function _toPropertyKey(t) {
	var i = _toPrimitive(t, "string");
	return "symbol" == typeof i ? i : i + "";
}
function _toPrimitive(t, r) {
	if ("object" != typeof t || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != typeof i) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
function _setFunctionName(e, t, n) {
	"symbol" == typeof t && (t = (t = t.description) ? "[" + t + "]" : "");
	try {
		Object.defineProperty(e, "name", {
			configurable: !0,
			value: n ? n + " " + t : t
		});
	} catch (e) {}
	return e;
}
function _checkInRHS(e) {
	if (Object(e) !== e) throw TypeError("right-hand side of 'in' should be an object, got " + (null !== e ? typeof e : "null"));
	return e;
}
let _DefaultDuoAgentPlatf;
(class {
	static {
		[_DefaultDuoAgentPlatf, _initClass] = _applyDecs(this, [Injectable(DuoAgentPlatformTracker, [
			ConfigService,
			SnowplowService,
			StandardContext,
			Logger
		])], []).c;
	}
	#configService;
	#snowplowService;
	#standardContext;
	#logger;
	#clientContext = createSnowplowClientContext();
	#enabled = true;
	constructor(configService, snowplowService, standardContext, logger) {
		this.#configService = configService;
		this.#snowplowService = snowplowService;
		this.#configService.onConfigChange((config) => this.#reconfigure(config));
		this.#standardContext = standardContext;
		this.#logger = withPrefix(logger, "[DuoAgentPlatformTracker]");
	}
	#reconfigure(config) {
		const enabled = config.telemetry?.enabled;
		if (typeof enabled !== "undefined" && this.#enabled !== enabled) {
			this.#enabled = enabled;
			if (enabled === false) this.#logger.warn(`Telemetry is disabled. Please, consider enabling telemetry to improve our service.`);
			else if (enabled === true) this.#logger.info(`Telemetry is enabled.`);
		}
		this.#setClientContext({
			extension: config.telemetry?.extension,
			ide: config.telemetry?.ide
		});
	}
	#setClientContext(context) {
		this.#clientContext.data = buildSnowplowClientContextData(context);
	}
	async trackEvent(event, context) {
		const structuredEvent = {
			category: DUO_AGENT_PLATFORM_CATEGORY,
			action: event,
			value: context?.workflowId ? (() => {
				const parsed = parseInt(String(context.workflowId), 10);
				return Number.isFinite(parsed) ? parsed : void 0;
			})() : void 0
		};
		try {
			const extra = {};
			Object.entries(context || {}).forEach(([key, value]) => {
				extra[key] = value;
			});
			const standardContext = this.#standardContext.build(extra);
			const contexts = [standardContext, this.#clientContext];
			if (!this.#snowplowService.validateContext(standard_context_schema_1_1_1_default, standardContext.data)) return;
			if (!this.#snowplowService.validateContext(ide_extension_version_1_1_0_exports, this.#clientContext?.data)) return;
			await this.#snowplowService.trackStructuredEvent(structuredEvent, contexts);
			this.#logger.debug(`Successfully tracked telemetry event: ${structuredEvent.action}`);
		} catch (error) {
			this.#logger.warn(`Failed to track telemetry event: ${event}`, error);
		}
	}
	isEnabled() {
		return this.#enabled;
	}
	static {
		_initClass();
	}
});
//#endregion
export { DEFAULT_SNOWPLOW_OPTIONS, DEFAULT_TRACKING_ENDPOINT, DUO_AGENT_PLATFORM_CATEGORY, DUO_CHAT_CATEGORY, DUO_CHAT_EVENT, DUO_WORKFLOW_EVENT, _DefaultDuoAgentPlatf as DefaultDuoAgentPlatformTracker, _DefaultExtensionActi as DefaultExtensionActivitySnowplowTracker, _DefaultSnowplowServi as DefaultSnowplowService, _DefaultStandardConte as DefaultStandardContext, DuoAgentPlatformEvent, DuoAgentPlatformTracker, DuoChatSnowplowTracker, DuoWorkflowTracker, ENVIRONMENT_NAMES, ENVIRONMENT_URLS, EVENT_VALIDATION_ERROR_MSG, EXTENSION_ACTIVITY_CATEGORY, EXTENSION_ACTIVITY_EVENT, Emitter, ExtensionActivitySnowplowTracker, GC_TIME, IDE_EXTENSION_VERSION_SCHEMA, ide_extension_version_1_1_0_default as IdeExtensionContextSchema, MIN_VERSION_FOR_EVENT_FORWARDING, OTelService, PerformanceInteractionType, SAAS_INSTANCE_URL, STANDARD_CONTEXT_SCHEMA, SnowplowService, StandardContext, standard_context_schema_1_1_1_default as StandardContextSchema, TELEMETRY_NOTIFICATION, buildSnowplowClientContextData, createSnowplowClientContext, environmentFromHost, getEventForwarderEndpoint, isTrackFeedbackContext, supportsEventForwarding };

//# sourceMappingURL=index.mjs.map